import { createReducer, on } from '@ngrx/store';

import { PolicyActionsUnion } from '@app/stp/state/actions/policy.actions';
import { PolicyActions } from '@app/stp/state/actions';

import { ChangeTypeEnum } from '@global/infrastructure/enums/change-type.enum';

import { GwDate } from '@global/infrastructure/interfaces/app-dates';
import { GwHelpers } from '@global/infrastructure/helpers/gw-helpers';
import { KeyValueDict } from '@global/infrastructure/interfaces/key-value-dict';

export interface PolicyState {
  initialState: {
    policyDetailsVerify: any;
    policyDetails: any;
    isLoaded: boolean;
    error: any;
  };
  requestPayload: {
    premium: any;
    error?: any;
  };
  finalState: {
    boundPolicy: any;
    isNewDirectDebit?: boolean;
  };
  errors?: {
    isGatewayTimeout?: boolean;
    isInvalidPromoCode?: boolean;
    invalidNextStartDate?: {
      effectiveDate: Date,
      earliestNextStartDate: Date,
      previousChangeType: ChangeTypeEnum
    }
  };
  changes: KeyValueDict<any>;
}

export const policyState: PolicyState = {
  initialState: {
    policyDetailsVerify: null,
    policyDetails: null,
    isLoaded: false,
    error: null
  },
  requestPayload: {
    premium: null,
    error: null
  },
  finalState: {
    boundPolicy: null,
    isNewDirectDebit: false
  },
  errors: {
    isGatewayTimeout: false,
    isInvalidPromoCode: false,
    invalidNextStartDate: {
      effectiveDate: null,
      earliestNextStartDate: null,
      previousChangeType: null
    }
  },
  changes: null
};

const policyReducer = createReducer(
  policyState,
  on(PolicyActions.loadPolicy, state => ({ ...state, initialState: { ...state.initialState, isLoaded: false}})),
  on(PolicyActions.loadPolicyVerifySucceeded, updateVerifyStateOnLoadSuccess),
  on(PolicyActions.loadPolicySucceeded, updateInitialStateOnLoadSuccess),
  on(PolicyActions.retrieveQuoteSucceeded, updateInitialStateOnLoadSuccess),
  on(PolicyActions.billingSucceeded, updateInitialStateOnBillingSuccess),
  on(PolicyActions.billingFailed, updateInitialStateOnFailure),
  on(PolicyActions.loadPolicyFailed, updateInitialStateOnFailure),
  on(PolicyActions.retrieveQuoteFailed, updateInitialStateOnFailure),
  on(PolicyActions.resetLoadPolicyError, state => ({...state, initialState: {...state.initialState, error: null, isLoaded: false}})),
  on(PolicyActions.resetPayloadChanges, state => ({...state, changes: null})),
  on(PolicyActions.resetDriversInPayloadChanges, resetDriversInPayloadChanges),
  on(PolicyActions.replaceRequestPayloadWithNewQuote, (state, {premResult}) => ({
    ...state,
    requestPayload: {premium: premResult, error: null},
    errors: {...state.errors, isGatewayTimeout: false, isInvalidPromoCode: false}
  })),
  on(PolicyActions.updateRequestPayloadError, (state, {error}) => ({
    ...state,
    requestPayload: {...state.requestPayload, error}
  })),
  on(PolicyActions.updatePayloadChanges, (state, {data}) => ({...state, changes: {...state.changes, ...data}})),

  on(PolicyActions.updateDriver, updateDriver),
  on(PolicyActions.updateBoundPolicy, (state, {boundPolicy}) => ({...state, finalState: {...state.finalState, boundPolicy}})),
  on(PolicyActions.updateChangeTypeAndEffectiveDatesOfRequestPayload, updateChangeTypeAndEffectiveDates),
  on(PolicyActions.updateCreditCardDetailsOfRequestPayload, updateCreditCardDetails),
  on(PolicyActions.updateDirectDebitDetailsOfRequestPayload, updateDirectDebitDetails),
  on(PolicyActions.updatePaymentDatePayload, updatePaymentDate),
  on(PolicyActions.resetPolicyStateErrors, state => ({
    ...state,
    errors: {...state.errors, isGatewayTimeout: false, isInvalidPromoCode: false}
  })),
  on(PolicyActions.updateGatewayTimeoutError, state => ({...state, errors: {...state.errors, isGatewayTimeout: true}})),
  on(PolicyActions.updateInvalidPromoCodeError, state => ({
    ...state,
    errors: {...state.errors, isInvalidPromoCode: true}
  })),
  on(PolicyActions.redirectToOutsideValidDurationHardStop, (state, {
    effectiveDate,
    earliestNextStartDate,
    previousChangeType
  }) => ({
    ...state,
    errors: {...state.errors, invalidNextStartDate: {effectiveDate, earliestNextStartDate, previousChangeType}}
  })),
  on(PolicyActions.removeTempBlock, removeTempBlock),
  on(PolicyActions.updateNewDirectDebitRequirement, state => ({
    ...state,
    finalState: {...state.finalState, isNewDirectDebit: true}
  })),
);

export function reducer(state: PolicyState | undefined, action: PolicyActionsUnion) {
  return policyReducer(state, action);
}

function updateVerifyStateOnLoadSuccess(state, {data: policyDetailsVerify}) {
  return {
    ...state,
    initialState: {
      policyDetailsVerify,
      policyDetails: {},
      error: null
    },
  };
}

function updateInitialStateOnLoadSuccess(state, {data: policyDetails}) {
  return {
    ...state,
    initialState: {
      policyDetailsVerify : {
        ...state?.initialState.policyDetailsVerify,
      },
      isLoaded: true,
      policyDetails, error: null},
    requestPayload: {premium: updateMainPolicyHolderDetails(policyDetails.result), error: null}
  };
}

function updateInitialStateOnBillingSuccess(state, {data}) {
  return {
    ...state,
    initialState: {policyDetails: {result: data}, error: null},
    requestPayload: {premium: data, error: null}
  };
}

function updateInitialStateOnFailure(state, {error}) {
  return {
    ...state,
    initialState: {error, policyDetails: null},
    requestPayload: {premium: null}
  };
}

function updateCreditCardDetails(state: PolicyState, {creditCardDetails_dlg}) {
  const {bindData} = state.requestPayload.premium;

  return {
    ...state,
    error: null,
    requestPayload: {
      ...state.requestPayload,
      premium: {
        ...state.requestPayload.premium,
        bindData: {
          ...bindData,
          creditCardDetails_dlg
        }
      }
    }
  };
}

function updatePaymentDate(state: PolicyState, {paymentDate}) {

  const {requestPayload: {premium}, requestPayload: {premium: {paymentDetails}}} = state;
  
  return {
    ...state,
    error: null,
    requestPayload: {
      ...state.requestPayload,
      premium: {
        ...premium,
        paymentDetails: {
          ...paymentDetails,
          preferredPaymentDate: parseInt(paymentDate)
        }
      }
    }
  };
}

function updateDirectDebitDetails(state: PolicyState, {data}) {
  let person;
  const {
    prefix,
    firstName,
    lastName,
    displayName,
    dateOfBirth,
    publicID
  } = state.initialState.policyDetails.result.accountHolder;
  let {primaryAddress: billingAddres} = state.initialState.policyDetails.result.accountHolder;

  const {requestPayload: {premium}, requestPayload: {premium: {paymentDetails}}} = state;
  const {
    accountHolder: {accountInName, isMorethanOnePersonAuthorized},
    bankAccountDetails: {bankAccountNumber, institutionName, bankSortCode, barredInd, verified},
    cardholderAddressForm, directDebitThirdParty, address, numberOfSignatures, preferredPaymentDate
  } = data;

  if (directDebitThirdParty) {
    person = {
      dateOfBirth: GwHelpers.convertToGwDateObject(directDebitThirdParty.dateOfBirth),
      firstName: directDebitThirdParty.firstName,
      lastName: directDebitThirdParty.lastName,
      prefix: directDebitThirdParty.prefix.toLowerCase(),
    };
  } else {
    person = {
      dateOfBirth,
      displayName,
      firstName,
      lastName,
      prefix,
      publicID
    };
  }

  if (cardholderAddressForm.isPolicyAddress.key === 'no') {
    const {
      addressLine1, addressLine2, addressLine3, town, county, postCode,
      uDPRN_dlg, uPRN_dlg, latitude_dlg, longitude_dlg, xcordinate_dlg, ycordinate_dlg,
    } = address.addressDetails.value;

    billingAddres = {
      addressType: 'billing',
      addressLine1: address.addressLine1 !== '' ? address.addressLine1 : addressLine1,
      addressLine2: address.addressLine2 !== '' ? address.addressLine2 : addressLine2,
      addressLine3,
      city: town,
      county_dlg: county,
      postalCode: postCode,
      country: 'GB',
      uDPRN_dlg,
      uPRN_dlg,
      latitude_dlg,
      longitude_dlg,
      xcordinate_dlg,
      ycordinate_dlg
    };
  }

  return {
    ...state,
    error: null,
    requestPayload: {
      ...state.requestPayload,
      premium: {
        ...premium,
        paymentDetails: {
          ...paymentDetails,
          directDebitDetails: {
            bankDetails: {
              accountNumber: bankAccountNumber,
              institutionName,
              sortCode: bankSortCode.join(''),
              barredInd
            },
            billingAddres,
            person,
            preferredPaymentDate: preferredPaymentDate,
            bankValidationPassedInd: verified,
            sameNameAndAddressInd: cardholderAddressForm.isPolicyAddress.key === 'yes',
            numberOfSignatures: isMorethanOnePersonAuthorized === 'Yes' ? parseInt(numberOfSignatures.numberOfSignatures, 10) : 1,
            isTelephoneSignUpRequired: accountInName === 'Yes' && isMorethanOnePersonAuthorized === 'No',
            isPolicyHolderPayerInd: accountInName === 'Yes'
          }
        }
      }
    }
  };
}

function updateMainPolicyHolderDetails(premium: any) {
  const {baseData: {accountHolder, legacyPolicyInd_Dlg}} = premium;
  const {lobData: {mOTLine_Ext}} = premium;
  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}}}} = premium;

  const indexOfMainPolicyHolder = drivers.findIndex(pH => pH.isPolicyHolder === true);
  const mainPolicyHolder = drivers[indexOfMainPolicyHolder];

  const accountMaritalStatus = legacyPolicyInd_Dlg && accountHolder.maritalStatusShadow_dlg ? accountHolder.maritalStatusShadow_dlg[0] : accountHolder.maritalStatus;
  const accountEmploymentStatus = legacyPolicyInd_Dlg && accountHolder.employmentStatusShadow_dlg ? accountHolder.employmentStatusShadow_dlg[0] : accountHolder.employmentStatus_dlg;
  const accountBusinessType = legacyPolicyInd_Dlg && accountHolder.businessTypeShadow_dlg ? accountHolder.businessTypeShadow_dlg[0] : accountHolder.businessType_dlg;
  const accountOccupation = legacyPolicyInd_Dlg && accountHolder.occupationShadow_dlg ? accountHolder.occupationShadow_dlg[0] : accountHolder.occupation_dlg;

  const updatedMainPolicyHolderDetails = {
    ...mainPolicyHolder,
    employmentStatus_dlg: accountEmploymentStatus,
    businessType_dlg: accountBusinessType,
    occupation_dlg: accountOccupation,
    person: {
      ...mainPolicyHolder.person,
      lastName: accountHolder.lastName,
      firstName: accountHolder.firstName,
      maritalStatus: accountMaritalStatus
    }
  };

  const newDrivers = [...drivers];
  newDrivers[indexOfMainPolicyHolder] = updatedMainPolicyHolderDetails;

  return {
    ...premium,
    lobData: {
      ...premium.lobData,
      mOTLine_Ext: {
        ...mOTLine_Ext,
        coverables: {
          drivers: [
            ...newDrivers
          ],
          vehicleDrivers,
          motVehicles,
        }
      }
    },
    isDriverChanged_Dlg: true
  };
}

function updateChangeTypeAndEffectiveDates(
  state: PolicyState,
  {
    changeType,
    effectiveDate,
    expirationDate_dlg,
    isQuoteRetrieved
  }: { changeType: ChangeTypeEnum, effectiveDate: GwDate, expirationDate_dlg?: GwDate, isQuoteRetrieved: boolean }) {

  const changes = changeType === ChangeTypeEnum.Temporary ?
    {isTemporaryChange_Dlg: true, isPermanentChange_Dlg: false} :
    {isTemporaryChange_Dlg: false, isPermanentChange_Dlg: true};

  const premium = state.requestPayload.premium;
  const dates = expirationDate_dlg ? {effectiveDate, expirationDate_dlg} : {effectiveDate};

  if (!isQuoteRetrieved) {
    return {
      ...state,
      error: null,
      requestPayload: {
        ...state.requestPayload,
        premium: {
          ...premium,
          ...changes,
          baseData: {
            ...premium.baseData,
            ...dates
          }
        }
      }
    };
  }

  const {lobData: {mOTLine_Ext: {coverables: {drivers, motVehicles, vehicleDrivers}, offerings}}} = premium;
  const tempDrivers = drivers.filter(d => d.driverType === ChangeTypeEnum.Temporary);
  const tempVehicles = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary);

  return {
    ...state,
    error: null,
    requestPayload: {
      ...state.requestPayload,
      premium: {
        ...premium,
        ...changes,
        baseData: {
          ...premium.baseData,
          ...dates
        },
        lobData: {
          ...premium.lobData,
          mOTLine_Ext: {
            ...premium.lobData.mOTLine_Ext,
            offerings,
            coverables: {
              ...premium.lobData.mOTLine_Ext.coverables,
              vehicleDrivers,
              drivers: [
                ...drivers.filter(d => d.driverType === ChangeTypeEnum.Permanent),
                ...(tempDrivers.length > 0
                    ? tempDrivers.map(d => ({...d, driverStartDate: effectiveDate, driverEndDate: expirationDate_dlg}))
                    : []
                )
              ],
              motVehicles: [
                ...motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Permanent),
                ...(tempVehicles.length > 0
                    ? tempVehicles.map(t => ({
                      ...t,
                      vehicle: {...t.vehicle, vehicleStartDate: effectiveDate, vehicleEndDate: expirationDate_dlg}
                    }))
                    : []
                )
              ]
            }
          }
        }
      }
    }
  };
}

function removeTempBlock(state: PolicyState): PolicyState {
  const { lobData: { mOTLine_Ext: { coverables, ...mOTLine_Ext_Rest } } } = state.requestPayload.premium;
  const { drivers, motVehicles, vehicleDrivers } = coverables;

  // TODO: consider what if the main driver is removed
  const tempDriverIds = drivers.filter(d => d.driverType === ChangeTypeEnum.Temporary).map(d => d.publicID);
  const tempVehicleIds = motVehicles.filter(v => v.vehicle.typeOfVehicle === ChangeTypeEnum.Temporary).map(v => v.vehicle.publicID);

  return {
    ...state,
    requestPayload: {
      ...state.requestPayload,
      premium: {
        ...state.requestPayload.premium,
        lobData: {
          ...state.requestPayload.premium.lobData,
          mOTLine_Ext: {
            ...mOTLine_Ext_Rest,
            coverables: {
              ...coverables,
              vehicleDrivers: vehicleDrivers
                .filter(vd => tempVehicleIds.findIndex(vid => vid === vd.vehicleID) < 0)
                .map(vd => ({
                  ...vd,
                  driversID: vd.driversID.filter(id => tempDriverIds.findIndex(tid => tid === id) < 0)
                })),
              drivers: drivers.filter(d => d.driverType !== ChangeTypeEnum.Temporary),
              motVehicles: motVehicles.filter(v => v.vehicle.typeOfVehicle !== ChangeTypeEnum.Temporary).map(v => {
                const { vehicle: { schItems, ...vehicleWithoutSchItems }, ...rest } = v;
                return { ...rest, vehicle: vehicleWithoutSchItems };
              })
            }
          }
        }
      }
    }
  };
}

function updateDriver(state: PolicyState, driver) {
  const {
    lobData: {
      mOTLine_Ext: { coverables, ...mOTLine_Ext_Rest },
    },
  } = state.requestPayload.premium;
  const { drivers } = coverables;

  const updatedDriver = {
    ...drivers.find((d) => d.fixedId === driver.data.fixedId),
    ...driver.data,
  };
  const updatedDrivers = [
    ...drivers.filter((d) => d.fixedId !== driver.data.fixedId),
    updatedDriver,
  ];

  return {
    ...state,
    requestPayload: {
      premium: {
        ...state.requestPayload.premium,
        lobData: {
          ...state.requestPayload.premium.lobData,
          mOTLine_Ext: {
            ...mOTLine_Ext_Rest,
            coverables: {
              ...coverables,
              drivers: updatedDrivers,
            },
          },
        },
      },
    },
  };
}

function resetDriversInPayloadChanges(state: PolicyState) {
  // TODO: investigate why this fails on perm journey
  if (state.changes?.temporaryDriver) {
    return {...state, changes: {...state.changes, temporaryDriver: {...state.changes.temporaryDriver, newDrivers: []}}};
  }
  return state;
}
